import React, { useState } from "react";
import { AlertCircle, CheckCircle2, Download, FileCheck2 } from "lucide-react";
import { Button } from "../ui";
import { getDocumentLabel } from "../../lib/documentTypes";
import { leadsAPI } from "../../services/api";

const PRIORITY_TYPES = ["APPEAL_FORM", "DEPRECIATION_SCHEDULE", "COVER_PAGE"];

/**
 * Post-audit savings panel (preview at AWAITING_ACCOUNT) and post-FILED success
 * panel with evidence packet downloads.
 */
export default function FilingSuccessPanel({
  status,
  variant = "filed",
  sessionId,
  jobId,
  businessId,
  assessmentNoticeUrl,
  assetLedgerUrl,
}) {
  const isPreview = variant === "preview" || status?.state === "AWAITING_ACCOUNT";
  const isFiled = status?.state === "FILED";

  const [leadForm, setLeadForm] = useState({
    businessEmail: "",
    companyName: "",
    contactPhone: "",
  });
  const [leadState, setLeadState] = useState({
    isSubmitting: false,
    error: null,
    submitted: false,
  });

  if (!status || (!isFiled && !isPreview)) return null;

  const reduction = status.assessedDelta ? Number(status.assessedDelta) : null;
  const savings = status.estimatedTaxSavings ? Number(status.estimatedTaxSavings) : null;

  const documents = status.documents ?? [];
  const ordered = [
    ...PRIORITY_TYPES.map((type) => documents.find((d) => d.type === type)).filter(Boolean),
    ...documents.filter((d) => !PRIORITY_TYPES.includes(d.type)),
  ];

  const canSubmitLead =
    leadForm.businessEmail.trim() &&
    leadForm.companyName.trim() &&
    leadForm.contactPhone.trim() &&
    sessionId &&
    jobId &&
    !leadState.isSubmitting &&
    !leadState.submitted;

  const handleLeadSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmitLead) return;

    setLeadState({ isSubmitting: true, error: null, submitted: false });
    try {
      await leadsAPI.capture({
        sessionId,
        jobId,
        businessEmail: leadForm.businessEmail.trim(),
        companyName: leadForm.companyName.trim(),
        contactPhone: leadForm.contactPhone.trim(),
        businessId: businessId?.trim() || undefined,
        assessmentNoticeUrl: assessmentNoticeUrl?.trim() || undefined,
        assetLedgerUrl: assetLedgerUrl?.trim() || undefined,
      });
      setLeadState({ isSubmitting: false, error: null, submitted: true });
    } catch (err) {
      setLeadState({
        isSubmitting: false,
        error: err,
        submitted: false,
      });
    }
  };

  return (
    <div
      className={[
        "border-2 bg-slate-950",
        isPreview ? "border-cyan-500/50" : "border-emerald-500/50",
      ].join(" ")}
    >
      <div
        className={[
          "border-b px-5 py-3 flex items-center gap-2",
          isPreview
            ? "border-cyan-500/30 bg-cyan-950/30"
            : "border-emerald-500/30 bg-emerald-950/30",
        ].join(" ")}
      >
        {isPreview ? (
          <CheckCircle2 className="h-5 w-5 text-cyan-400" />
        ) : (
          <FileCheck2 className="h-5 w-5 text-emerald-400" />
        )}
        <span
          className={[
            "text-sm font-semibold tracking-wide uppercase",
            isPreview ? "text-cyan-300" : "text-emerald-300",
          ].join(" ")}
        >
          {isPreview
            ? "Notice Received. Analyzing Asset Classifications..."
            : "Appeal Filed: Evidence Package Ready"}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-800">
        <div className="bg-slate-950 px-5 py-6">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            Calculated Assessment Reduction
          </p>
          <p className="mt-2 text-3xl font-bold tabular-nums text-white">
            {reduction != null && !Number.isNaN(reduction)
              ? `$${reduction.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
              : "-"}
          </p>
        </div>
        <div className="bg-slate-950 px-5 py-6">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
            Estimated Tax Savings
          </p>
          <p
            className={[
              "mt-2 text-3xl font-bold tabular-nums",
              isPreview ? "text-cyan-400" : "text-emerald-400",
            ].join(" ")}
          >
            {savings != null && !Number.isNaN(savings)
              ? `$${savings.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
              : "-"}
          </p>
          <p className="text-xs text-slate-500 mt-1">35% success fee applies only if realized</p>
        </div>
      </div>

      {isPreview && (
        <form onSubmit={handleLeadSubmit} className="px-5 py-6 border-t border-slate-800 space-y-5">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Notice Received. Analyzing Asset Classifications...
            </h3>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed">
              Our compliance engine is auditing your equipment lines against state depreciation
              rules. Enter your corporate details below to receive your optimization report via
              email.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block sm:col-span-2">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Business Email <span className="text-red-400">*</span>
              </span>
              <input
                type="email"
                value={leadForm.businessEmail}
                onChange={(e) =>
                  setLeadForm((prev) => ({ ...prev, businessEmail: e.target.value }))
                }
                placeholder="you@company.com"
                className="mt-1.5 w-full border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                required
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Company Name <span className="text-red-400">*</span>
              </span>
              <input
                type="text"
                value={leadForm.companyName}
                onChange={(e) => setLeadForm((prev) => ({ ...prev, companyName: e.target.value }))}
                placeholder="Acme Manufacturing LLC"
                className="mt-1.5 w-full border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                required
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                Contact Phone <span className="text-red-400">*</span>
              </span>
              <input
                type="tel"
                value={leadForm.contactPhone}
                onChange={(e) => setLeadForm((prev) => ({ ...prev, contactPhone: e.target.value }))}
                placeholder="(314) 555-0100"
                className="mt-1.5 w-full border border-slate-600 bg-slate-900 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-600 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500"
                required
              />
            </label>
          </div>

          {leadState.error && (
            <div className="flex items-start gap-3 border border-red-500/40 bg-red-950/30 px-4 py-3 text-sm text-red-300">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-medium">Could not submit your request</p>
                <p className="mt-1 opacity-90">{leadState.error.message}</p>
              </div>
            </div>
          )}

          {leadState.submitted ? (
            <div className="flex items-start gap-3 border border-emerald-500/40 bg-emerald-950/30 px-4 py-3 text-sm text-emerald-200">
              <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-emerald-400" />
              <p>
                Request received. We will email your verified savings report within one business
                day.
              </p>
            </div>
          ) : (
            <Button
              type="submit"
              variant="cta"
              size="lg"
              className="w-full sm:w-auto bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold border-0"
              disabled={!canSubmitLead}
              isLoading={leadState.isSubmitting}
            >
              Request My Verified Savings Report
            </Button>
          )}
        </form>
      )}

      {!isPreview && ordered.length > 0 && (
        <div className="px-5 py-4 border-t border-slate-800">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3">
            Evidence Package Components
          </p>
          <div className="space-y-2">
            {ordered.map((doc) => (
              <a
                key={`${doc.type}-${doc.url}`}
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="flex items-center justify-between gap-3 border border-slate-700 bg-slate-900 px-4 py-3 text-sm text-slate-200 hover:border-cyan-500/50 hover:bg-slate-900/80 transition-colors group"
              >
                <span className="font-medium">{getDocumentLabel(doc.type)}</span>
                <span className="flex items-center gap-1.5 text-cyan-400 text-xs font-semibold uppercase tracking-wide group-hover:text-cyan-300">
                  <Download className="h-4 w-4" />
                  Download
                </span>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
